/**
 * DRPX Courier — i18n Provider
 *
 * Lazy-Load von 7 Sprachdateien via Vite Code-Splitting.
 * `useT()` liefert die `t`-Funktion mit Zusatz-Properties
 * (locale, lang, setLang, loading).
 *
 * @module i18n/index
 */

import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { safeGetLS, safeSetLS } from '../lib/storage.js';
import { _sb } from '../lib/supabase.js';
import type { TranslateFn } from '../types.js';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

/** Unterstützte Sprachcodes. */
export type LangCode = 'de' | 'en' | 'pl' | 'bg' | 'ro' | 'tr' | 'ru';

/** Erweiterte Translate-Funktion mit Metadaten. */
export interface TFunction extends TranslateFn {
  locale: string;
  lang: LangCode;
  setLang: (lang: LangCode) => void;
  loading: boolean;
}

// ═══════════════════════════════════════════════════════════════
// Locale-Mapping & Lazy-Loaders
// ═══════════════════════════════════════════════════════════════

export const LOCALE_MAP: Record<LangCode, string> = {
  de: 'de-DE',
  en: 'en-US',
  pl: 'pl-PL',
  bg: 'bg-BG',
  ro: 'ro-RO',
  tr: 'tr-TR',
  ru: 'ru-RU',
};

type LangLoader = () => Promise<{ default: Record<string, string> }>;

const LANG_LOADERS: Record<LangCode, LangLoader> = {
  de: () => import('./de.js'),
  en: () => import('./en.js'),
  pl: () => import('./pl.js'),
  bg: () => import('./bg.js'),
  ro: () => import('./ro.js'),
  tr: () => import('./tr.js'),
  ru: () => import('./ru.js'),
};

// ═══════════════════════════════════════════════════════════════
// Übersetzen in eine FREMDE Sprache (19.09.2026)
// ═══════════════════════════════════════════════════════════════

const translatorCache = new Map<LangCode, Promise<TranslateFn>>();

/**
 * Liefert eine Übersetzungsfunktion für eine beliebige Sprache — nicht die
 * eigene. Gebraucht für Benachrichtigungen: Glocke und Mail müssen in der
 * Sprache des EMPFÄNGERS stehen, gebaut werden sie aber in der App des
 * Absenders.
 *
 * Die Sprachdatei wird beim ersten Aufruf nachgeladen und danach aus dem
 * Zwischenspeicher genommen. Unbekannte Sprachcodes und Ladefehler fallen
 * auf Deutsch zurück — eine Meldung in der falschen Sprache ist besser als
 * gar keine.
 */
export function loadTranslator(lang: string | null | undefined): Promise<TranslateFn> {
  const code: LangCode = lang && lang in LANG_LOADERS ? (lang as LangCode) : 'de';
  const cached = translatorCache.get(code);
  if (cached) return cached;
  const p = LANG_LOADERS[code]()
    .then((mod): TranslateFn => {
      const strings = mod.default;
      return (key: string): string => strings[key] || key;
    })
    .catch((): Promise<TranslateFn> => {
      translatorCache.delete(code);
      return code === 'de'
        ? Promise.resolve((key: string): string => key)
        : loadTranslator('de');
    });
  translatorCache.set(code, p);
  return p;
}

// ═══════════════════════════════════════════════════════════════
// Context & Provider
// ═══════════════════════════════════════════════════════════════

export const I18nContext = createContext<TFunction | null>(null);

interface I18nProviderProps {
  children: React.ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps): React.JSX.Element | null {
  const [lang, setLang] = useState<LangCode>(() => {
    const stored = safeGetLS('drpx_lang');
    return (stored && stored in LANG_LOADERS) ? stored as LangCode : 'de';
  });
  const [strings, setStrings] = useState<Record<string, string> | null>(null);
  const [loading, setLoading] = useState(true);

  // Load language on change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    LANG_LOADERS[lang]().then((mod) => {
      if (!cancelled) {
        setStrings(mod.default);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const changeLang = useCallback((newLang: LangCode) => {
    if (!LANG_LOADERS[newLang]) return;
    setLang(newLang);
    safeSetLS('drpx_lang', newLang);

    // Best-effort: Sprache zusätzlich in profiles.lang speichern (Migration 039),
    // damit Sender ihre Push-/E-Mail-Texte in der Sprache des Empfängers
    // generieren können. Fehler werden bewusst geschluckt — der UI-Wechsel
    // hat bereits stattgefunden, eine fehlgeschlagene DB-Sync soll
    // den User nicht stören.
    void (async function syncLangToDb() {
      try {
        const client = _sb; // Closure-safe Narrowing
        if (!client) return;
        const sess = await client.auth.getSession();
        const userId = sess?.data?.session?.user?.id;
        if (!userId) return; // nicht eingeloggt → nichts zu syncen
        await client.from('profiles').update({ lang: newLang }).eq('id', userId);
      } catch {
        /* ignored — UI-Wechsel ist wichtiger als DB-Sync */
      }
    })();
  }, []);

  // The `t` function — same API as before
  const t = useMemo((): TFunction => {
    const base = strings ?? {};
    return Object.assign(
      (key: string): string => base[key] || key,
      {
        locale: LOCALE_MAP[lang] || 'de-DE',
        lang,
        setLang: changeLang,
        loading,
      },
    );
  }, [strings, lang, changeLang, loading]);

  if (loading && !strings) {
    return null; // or a loading spinner
  }

  return (
    <I18nContext.Provider value={t}>
      {children}
    </I18nContext.Provider>
  );
}

/**
 * useT() — Zugriff auf die i18n-Funktion `t`.
 * Muss innerhalb von `<I18nProvider>` aufgerufen werden.
 */
export function useT(): TFunction {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useT() muss innerhalb von <I18nProvider> verwendet werden');
  }
  return ctx;
}
